import { useState } from 'react'
import { Pause, Play } from 'lucide-react'
import { Button } from '@/components/ds2/Button'
import { useAppSelector } from '@/hooks'
import { useRecordingPlayback } from '../hooks/useRecordingPlayback'

function formatTime(seconds: number): string {
    return `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`
}

/** Plays a voice session through Dispatch Help's recording endpoint, or a direct `url` (a broker call). */
export function RecordingPlayer({ tabId, messageId, url }: { tabId: string; messageId: string; url?: string }) {
    const viewerId = useAppSelector((state) => (state.auth.isAuthenticated ? (state.auth.user?.id ?? null) : null))
    const { audioRef, loading, error, togglePlay } = useRecordingPlayback(tabId, messageId, viewerId, url)
    const [playing, setPlaying] = useState(false)
    const [current, setCurrent] = useState(0)
    const [duration, setDuration] = useState(0)
    const [mediaError, setMediaError] = useState(false)

    return (
        <div className="rounded-lg bg-default px-2.5 py-2">
            <div className="flex items-center gap-2.5">
                <Button
                    type="button"
                    iconOnly
                    size="sm"
                    className="shrink-0"
                    disabled={!viewerId}
                    loading={loading}
                    onClick={() => {
                        setMediaError(false)
                        void togglePlay()
                    }}
                    aria-label={loading ? 'Loading recording' : playing ? 'Pause recording' : 'Play recording'}
                >
                    {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                </Button>
                <input
                    type="range"
                    min={0}
                    max={duration}
                    step={0.1}
                    value={Math.min(current, duration)}
                    disabled={!duration || loading}
                    onChange={(e) => {
                        const value = Number(e.target.value)
                        if (audioRef.current) audioRef.current.currentTime = value
                        setCurrent(value)
                    }}
                    className="h-1.5 flex-1 cursor-pointer accent-accent"
                    aria-label="Seek recording"
                />
                <span className="w-[72px] shrink-0 text-right text-[11px] tabular-nums text-foreground-muted">
                    {formatTime(current)} / {formatTime(duration)}
                </span>
            </div>
            {(error || mediaError) && (
                <p role="status" className="mt-1 text-xs text-foreground-muted">
                    {error || 'This browser could not play the recording.'}
                </p>
            )}
            <audio
                ref={audioRef}
                preload="none"
                onLoadedMetadata={(e) => setDuration(Number.isFinite(e.currentTarget.duration) ? e.currentTarget.duration : 0)}
                onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime)}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => setPlaying(false)}
                onEmptied={() => {
                    setPlaying(false)
                    setCurrent(0)
                    setDuration(0)
                }}
                onError={() => setMediaError(true)}
                className="hidden"
            />
        </div>
    )
}
